// AudioEditorHeader - Header control bar with main actions
import React from 'react';
import { Button, ActionIcon, Text } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave,
  faUpload,
  faSliders,
  faMagicWandSparkles,
  faArrowDown,
  faArrowLeft,
  faPencilAlt,
  faVolumeUp,
  faVolumeMute
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import styles from '../AudioEditor.module.css';

interface AudioEditorHeaderProps {
  // Project info
  projectTitle: string;
  isEditingProjectTitle: boolean;
  onProjectTitleChange: (editing: boolean) => void;
  onProjectTitleUpdate: (title: string) => void;
  
  // Navigation
  onBackClick: () => void;
  onTitleClick: () => void;
  
  // Actions
  onAIAudioClick: () => void;
  onUploadAudioClick: () => void;
  onSaveClick: () => void;
  onMixAudioClick: () => void;
  onExportClick?: () => void;
  
  // State
  isLoading: boolean;
  hasTracks: boolean;
  hasExportableAudio: boolean;
  
  // Master volume controls
  masterVolume: number;
  isMasterMuted: boolean;
  onMasterVolumeChange: (volume: number) => void;
  onMasterMuteToggle: () => void;
}

const AudioEditorHeader: React.FC<AudioEditorHeaderProps> = ({
  projectTitle,
  isEditingProjectTitle,
  onProjectTitleChange,
  onProjectTitleUpdate,
  onBackClick,
  onTitleClick,
  onAIAudioClick,
  onUploadAudioClick,
  onSaveClick,
  onMixAudioClick,
  onExportClick,
  isLoading,
  hasTracks,
  hasExportableAudio,
  masterVolume,
  isMasterMuted,
  onMasterVolumeChange,
  onMasterMuteToggle
}) => {
  const { t } = useTranslation('audioEditor');

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const newTitle = e.currentTarget.value.trim();
      if (newTitle) {
        onProjectTitleUpdate(newTitle);
      }
      onProjectTitleChange(false);
    } else if (e.key === 'Escape') {
      onProjectTitleChange(false);
    }
  };

  const handleTitleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const newTitle = e.currentTarget.value.trim();
    if (newTitle) {
      onProjectTitleUpdate(newTitle);
    }
    onProjectTitleChange(false);
  };

  return (
    <div className={styles.header}>
      <Button 
        className={styles.backButton}
        onClick={onBackClick}
        title={t('header.backButton')}
      >
        <FontAwesomeIcon icon={faArrowLeft} />
      </Button>
      
      <div className={styles.titleContainer}>
        {isEditingProjectTitle ? (
          <input
            type="text"
            defaultValue={projectTitle}
            onKeyDown={handleTitleKeyDown}
            onBlur={handleTitleBlur}
            className={styles.projectTitleInput}
            autoFocus
          />
        ) : (
          <>
            <h1 
              className={`${styles.pageTitle} ${styles.clickableTitle}`}
              onClick={onTitleClick}
              title={t('header.backButton')}
            >
              {t('header.title')}
            </h1>
            <div className={styles.projectNameContainer}>
              <h2 className={styles.projectName}>{projectTitle}</h2>
              <button 
                className={styles.editButton}
                onClick={() => onProjectTitleChange(true)}
                title={t('toolbar.settings')}
              >
                <FontAwesomeIcon icon={faPencilAlt} />
              </button>
            </div>
          </>
        )}
      </div>
      
      <div className={styles.headerActions}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginRight: '20px' }}>
          <Text size="sm">{t('controls.masterVolume')}</Text>
          <ActionIcon
            onClick={onMasterMuteToggle}
            className={isMasterMuted ? styles.muteButtonActive : styles.muteButton}
          >
            <FontAwesomeIcon icon={isMasterMuted ? faVolumeMute : faVolumeUp} />
          </ActionIcon>
          <input
            type="range"
            value={masterVolume * 100}
            onChange={(event) => onMasterVolumeChange(Number(event.target.value) / 100)}
            min={0}
            max={100}
            disabled={isMasterMuted}
            style={{
              width: '80px',
              height: '6px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
          <Text size="xs" style={{ minWidth: '35px', textAlign: 'center' }}>
            {Math.round(masterVolume * 100)}%
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginRight: '20px', flexWrap: 'wrap' }}>
        
          <Button
            leftSection={<FontAwesomeIcon icon={faMagicWandSparkles} />}
            onClick={onAIAudioClick}
            disabled={isLoading}
            className={styles.aiAudioButton}
            size="sm"
          >
            {t('generation.narration')}
          </Button>
          
          <Button
            leftSection={<FontAwesomeIcon icon={faUpload} />}
            onClick={onUploadAudioClick}
            disabled={isLoading}
            className={styles.uploadButton}
            size="sm"
          >
            {t('toolbar.upload')}
          </Button>
        
          <Button
            leftSection={<FontAwesomeIcon icon={faSave} />}
            onClick={onSaveClick}
            disabled={isLoading}
            className={styles.saveButton}
            size="sm"
          >
            {t('toolbar.save')}
          </Button>
        
          <Button
            leftSection={<FontAwesomeIcon icon={faSliders} />}
            onClick={onMixAudioClick}
            disabled={isLoading || !hasTracks}
            loading={isLoading}
            className={styles.mixAudioButton}
            size="sm"
          >
            {isLoading ? t('upload.uploading') : t('mixer.title')}
          </Button>
        
          {/* Export button when final audio is available */}
          {hasExportableAudio && onExportClick && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (!isLoading) onExportClick();
              }}
              className={styles.videoAvailableBadge}
              title={t('toolbar.download')}
              style={{
                position: 'relative',
                background: '#4caf50',
                color: 'white',
                borderRadius: '50%',
                width: '33px',
                height: '33px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.5)',
                transition: 'transform 0.2s ease',
                opacity: isLoading ? 0.6 : 1,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioEditorHeader;
